import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  computed,
  effect,
  signal,
  untracked,
  viewChild,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, take, timer } from 'rxjs';

import {
  AdvancePaymentController,
  PaymentDeleteConfirmationState,
} from '@shared-classes';
import {
  VOneTabsComponent,
  VOneInputComponent,
  VOneButtonComponent,
  VOneIconComponent,
} from '@shared-components';
import {
  PAYMENT_MANAGER_ROLES,
  PAYMENT_TAB_ID,
  STRING_CONSTANTS,
} from '@shared-constants';
import { StickyHeaderHeightDirective } from '@shared-directives';
import {
  AdvancePaymentAction,
  AdvancePaymentOwnerType,
  AdvancePaymentSubject,
  IconType,
  PaymentEntryType,
} from '@shared-enums';
import {
  GraphQLErrorResponse,
  LocalPaymentEntry,
  SelectOption,
  VOneTab,
} from '@shared-models';
import { AlertService } from '@shared-services';
import {
  advancePaymentTotal as advancePaymentTotalUtil,
  APPLIED_PAYMENT_STATUS,
  appliedPendingBalance as appliedPendingBalanceUtil,
  calculateAvailable,
  canDeletePayment as canDeletePaymentRule,
  findActiveCancellationId,
  getTodayIsoDate,
  toStoredDateString,
  hasAdvancePayment as hasAdvancePaymentUtil,
  DOCUMENT_STATE,
  hasAssociatedPayments as hasAssociatedPaymentsUtil,
  hasMultipleAdvancePayments as hasMultipleAdvancePaymentsUtil,
  INVOICE_BALANCE_EPSILON,
  isFullyPaid as isFullyPaidUtil,
  isInvoiceVoided,
  isPaymentApproved,
  round2,
  sortPaymentsByDate,
  sumAppliedPayments,
  sumPendingApprovalPayments,
  valueMatchesRoundedTarget,
} from '@shared-utils';

import {
  IInvoicePayments,
  IPaymentsList,
} from 'src/app/@interfaces/payments.interface';
import { IQuota, IQuotation } from 'src/app/@interfaces/quotation.interface';
import { ITransferDetails } from 'src/app/@interfaces/transfers.interface';
import { ITreasury } from 'src/app/@interfaces/treasury.interface';
import { PortfolioService } from 'src/app/@pages/portfolio/portfolio.service';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { UsersService } from 'src/app/@pages/users/users.service';
import { PaymentsPdfService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments-pdf.service';
import { PaymentsService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments.service';
import { TransfersService } from 'src/app/@shared/components/dialog/crud-transfers/transfers.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

interface ApprovalConfirmation extends ITransferDetails {
  isInvoiceComplete: boolean;
  value: number;
}

const APPROVAL_CONFIRMATION_DURATION_MS = 10000;

@Component({
  selector: 'v-one-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: [
    './payment-modal.component.scss',
    './payment-modal.component.responsive.scss',
  ],
  imports: [
    VOneTabsComponent,
    VOneInputComponent,
    VOneButtonComponent,
    VOneIconComponent,
    ReactiveFormsModule,
    CurrencyPipe,
    DatePipe,
    NgTemplateOutlet,
    TranslatePipe,
    StickyHeaderHeightDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentModalComponent implements OnInit, OnDestroy {
  translate = inject(TranslateService);
  private portfolio = inject(PortfolioService);
  private quotationService = inject(QuotationsService);
  private spinnerService = inject(NgxSpinnerService);
  private paymentsPdfService = inject(PaymentsPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  paymentsService = inject(PaymentsService);
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private usersService = inject(UsersService);
  private transfersService = inject(TransfersService);
  private readonly destroyRef = inject(DestroyRef);

  readonly idFactura = input(0);
  readonly idCustomer = input(0);
  readonly initialTabId = input<string>(PAYMENT_TAB_ID.REGISTER);
  readonly onClose = input<
    | ((result: {
        sended: boolean;
        refresh: boolean;
        notifyState: number | null;
      }) => void)
    | undefined
  >(undefined);
  readonly onPaymentRegistered = input<(() => void) | undefined>(undefined);
  readonly closeModal = input<(() => void) | undefined>(undefined);
  readonly invoiceVoided = input(false);
  readonly listBanks = signal<IPaymentsList[]>([]);
  readonly listCashiers = signal<IPaymentsList[]>([]);
  readonly advancePayments = signal<ITreasury[]>([]);
  readonly invoicePayments = input<IInvoicePayments[]>([]);
  readonly currentInvoicePayments = signal<IInvoicePayments[]>([]);
  readonly currentTabId = signal<string>(PAYMENT_TAB_ID.REGISTER);
  readonly originalInvoiceTotal = signal(0);
  readonly ncNdAdjustment = signal(0);
  readonly invoiceTotalLoaded = signal(false);
  readonly totalQuot = computed(
    () => this.originalInvoiceTotal() + this.ncNdAdjustment(),
  );
  readonly paidQuot = signal(0);
  readonly missingPaid = computed(() =>
    Math.max(0, this.totalQuot() - this.paidQuot()),
  );
  readonly totalPositivebalance = computed(() => {
    const remaining = this.totalQuot() - this.paidQuot();
    return remaining < 0 ? this.paidQuot() - this.totalQuot() : 0;
  });
  sended = false;
  refresh = false;
  private paidInvoiceTabsOnly = false;
  // Whether a payment was applied during this session, and the state the
  // invoice ended up in. Only a session that applied one notifies the
  // customer — a deletion that happens to leave the invoice partially paid
  // is not news.
  private paymentRegistered = false;
  // The payment state as the server resolved it. The modal used to derive
  // its own from the balance, which is how it ended up disagreeing with the
  // card over a cent. Null only while the invoice has not loaded, or if the
  // load failed — the getters below fall back to the old derivation there.
  readonly documentState = signal<number | null>(null);
  readonly quotationObj = signal({} as IQuota);
  readonly quotation = signal({} as IQuotation);
  readonly positiveBalanceValue = signal(0);

  @ViewChild('registerTab', { static: true })
  registerTab!: TemplateRef<unknown>;
  @ViewChild('approveTab', { static: true })
  approveTab!: TemplateRef<unknown>;
  @ViewChild('historyTab', { static: true })
  historyTab!: TemplateRef<unknown>;
  @ViewChild('invoiceDetailTab', { static: true })
  invoiceDetailTab!: TemplateRef<unknown>;

  readonly stickyHeader = viewChild(StickyHeaderHeightDirective);

  private readonly destinationControls = new Map<
    number,
    FormControl<number | null>
  >();
  private readonly originControls = new Map<
    number,
    FormControl<number | null>
  >();
  // Signals (not plain Map/Set) so isProcessingApproval()/approverName() —
  // read from the template — notify this OnPush component even though
  // they're written from a subscribe callback or a post-await continuation,
  // not a template event. Same reasoning as PaymentDeleteConfirmationState.
  private readonly approverNames = signal<ReadonlyMap<number, string>>(
    new Map(),
  );
  // Plain (not a signal) — only gates whether approverName() has already
  // kicked off a fetch for this id. Writing a signal synchronously from
  // approverName() itself (called from the template) throws NG0600, since
  // that write would happen mid-render; this Set never needs to notify the
  // template, only the approverNames signal write inside the subscribe
  // callback (which runs after the render pass) does.
  private readonly pendingApproverNameFetches = new Set<number>();
  private readonly processingApprovalIds = signal<ReadonlySet<number>>(
    new Set(),
  );
  readonly approvalConfirmation = signal<ApprovalConfirmation | null>(null);

  readonly transferDetailsByPayment = signal<Map<number, ITransferDetails>>(
    new Map(),
  );
  protected readonly iconType = IconType;
  protected readonly appliedPaymentStatus = APPLIED_PAYMENT_STATUS;

  private readonly deleteState = new PaymentDeleteConfirmationState();
  private readonly advancePayment = new AdvancePaymentController(this);

  tabs = signal<VOneTab[]>([]);

  readonly paymentValueControl = new FormControl<string | number | null>(null);
  readonly advanceValueControl = new FormControl<string | number | null>(null);
  readonly payFullAmount = signal<boolean>(false);

  readonly paymentMethodControl = new FormControl<number | null>(null);
  readonly bankOrCashierControl = new FormControl<number | null>(null);
  readonly dateControl = new FormControl<string>(getTodayIsoDate());
  readonly referenceControl = new FormControl<string>('');

  readonly addedPayments = signal<LocalPaymentEntry[]>([]);
  private nextLocalPaymentId = 0;

  readonly isSubmittingPayments = signal(false);
  readonly isAddingPayment = signal(false);
  private assignedPaymentIds = new Set<number>();
  readonly useAdvancePayment = signal<boolean>(false);
  readonly useCreditBalance = signal<boolean>(false);
  readonly selectedAdvancePaymentId = signal<number | null>(null);
  readonly useFullCreditBalanceAvailable = signal<boolean>(true);
  readonly useFullAdvanceAvailable = signal<boolean>(false);

  constructor() {
    this.paymentValueControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.syncPayFullAmountWithValue(value);
        this.paymentValueControl.setValue(value, { emitEvent: false });
      });

    this.advancePayment.mirrorValueControls(this.destroyRef);

    effect(() => {
      if (
        !this.useAdvancePayment() ||
        !this.useFullAdvanceAvailable() ||
        this.selectedAdvancePaymentId() === null
      ) {
        return;
      }
      const fullAmount = this.selectedAdvancePaymentAvailable;
      untracked(() => {
        if (
          !valueMatchesRoundedTarget(this.advanceValueControl.value, fullAmount)
        ) {
          this.advanceValueControl.setValue(fullAmount);
        }
      });
    });

    this.paymentMethodControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.useAdvancePayment()) {
          this.bankOrCashierControl.reset(null);
        }
      });
  }

  async ngOnInit(): Promise<void> {
    this.currentInvoicePayments.set(this.invoicePayments());
    this.currentTabId.set(this.initialTabId());
    this.buildTabs();

    try {
      // These three are independent reads (credit notes, debit notes, the
      // invoice itself) — run them concurrently instead of one after another
      // so the Invoice Detail tab (which only needs these three) isn't stuck
      // waiting on the sum of three round-trips.
      const [creditValues, debitValues, invoice] = await Promise.all([
        this.paymentsService.getCreditValues(this.idFactura()),
        this.paymentsService.getDebitsValues(this.idFactura()),
        this.paymentsService.getInvoice(this.idFactura()),
      ]);
      this.quotationObj.set(invoice);
      this.quotation.set(invoice);
      this.documentState.set(invoice.estado_factura ?? null);
      this.assignedPaymentIds = new Set(
        (this.quotationObj().payment ?? []).map((item) => item.id_payment),
      );
      // factura.total is the canonical total; the first sale line is the
      // legacy source, kept as a fallback while older rows still have it
      // unset. Same order the backend resolves it in (recalcInvoiceStatus),
      // so the modal and the stored state read the same number.
      this.originalInvoiceTotal.set(
        this.quotationObj().total ?? this.quotationObj().venta[0]?.total ?? 0,
      );
      this.ncNdAdjustment.set(debitValues - creditValues);
      this.invoiceTotalLoaded.set(true);
      let paidTotal = 0;
      this.currentInvoicePayments().forEach((element) => {
        paidTotal += element.value;
      });
      this.paidQuot.set(paidTotal);
      this.paidInvoiceTabsOnly =
        this.isFullyPaid && this.pendingApprovalPayments.length === 0;
      this.buildTabs();

      const [positiveBalanceValue] = await Promise.all([
        this.paymentsService.getPositiveBalance(this.idCustomer()),
        this.listsData(),
      ]);
      this.positiveBalanceValue.set(positiveBalanceValue);
      this.loadPersistedTransferDetails();
    } catch (error: unknown) {
      const message =
        (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
        (error as Error)?.message ||
        this.translate.instant('global.alerts.title.error');
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message,
        type: 'error',
        acceptText: 'Ok',
      });
    } finally {
      this.buildTabs();
    }
  }

  private buildTabs(): void {
    const tabs: VOneTab[] = [];
    if (!this.paidInvoiceTabsOnly) {
      tabs.push({
        id: PAYMENT_TAB_ID.REGISTER,
        label: this.translate.instant('global.payments.register-tab'),
        content: this.registerTab,
      });
    }
    if (this.canManagePayments && !this.paidInvoiceTabsOnly) {
      const approveTabLabel = this.translate.instant(
        'global.payments.approve-tab',
      );
      const pendingCount = this.pendingApprovalPayments.length;
      tabs.push({
        id: PAYMENT_TAB_ID.APPROVE,
        label:
          pendingCount > 0
            ? `${approveTabLabel} (${pendingCount})`
            : approveTabLabel,
        content: this.approveTab,
      });
    }
    tabs.push({
      id: PAYMENT_TAB_ID.HISTORY,
      label: this.translate.instant('global.payments.history-tab'),
      content: this.historyTab,
    });
    if (this.currentInvoicePayments().length > 0 || this.invoiceVoided()) {
      tabs.push({
        id: PAYMENT_TAB_ID.INVOICE_DETAIL,
        label: this.translate.instant('global.payments.detail-tab'),
        content: this.invoiceDetailTab,
      });
    }
    this.tabs.set(tabs);
  }

  get isAdmin(): boolean {
    return this.cookieAuthService.isAdmin;
  }

  get canManagePayments(): boolean {
    return PAYMENT_MANAGER_ROLES.includes(
      String(this.cookieAuthService.getRolId),
    );
  }

  get pendingApprovalPayments(): IInvoicePayments[] {
    return this.currentInvoicePayments().filter(
      (payment) => payment.status === 1 || payment.status === 2,
    );
  }

  get destinationOptions(): SelectOption[] {
    return [...this.listCashiers(), ...this.listBanks()].map((option) => ({
      value: option.id_payment,
      label: option.description,
    }));
  }

  isPendingApprovalForOperativo(payment: IInvoicePayments): boolean {
    return payment.status < APPLIED_PAYMENT_STATUS && !this.canManagePayments;
  }

  statusLabel(status: number): string {
    return isPaymentApproved(status)
      ? this.translate.instant('global.payments.status-approved')
      : this.translate.instant('global.payments.status-pending-approval');
  }

  valueLabel(payment: IInvoicePayments): string {
    const isCash = payment.paymentDetails?.type_payment === 1;
    return isCash
      ? this.translate.instant('global.payments.cash-label')
      : this.translate.instant('global.payments.transfer-label', {
          bank: payment.paymentType?.[0]?.description ?? '',
        });
  }

  approverName(payment: IInvoicePayments): string {
    const id = payment.id_user_approved;
    if (!id) {
      return '-';
    }
    if (
      !this.approverNames().has(id) &&
      !this.pendingApproverNameFetches.has(id)
    ) {
      this.pendingApproverNameFetches.add(id);
      this.usersService
        .getUser(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (user: { name?: string } | null) => {
            this.approverNames.update((current) =>
              new Map(current).set(id, user?.name ?? '-'),
            );
          },
          error: (error) => {
            console.error(`Error fetching approver name for user ${id}`, error);
            this.approverNames.update((current) =>
              new Map(current).set(id, '-'),
            );
          },
        });
    }
    return this.approverNames().get(id) ?? '-';
  }

  getDestinationControl(idPayinvoice: number): FormControl<number | null> {
    let control = this.destinationControls.get(idPayinvoice);
    if (!control) {
      control = new FormControl<number | null>(null);
      this.destinationControls.set(idPayinvoice, control);
    }
    return control;
  }

  getOriginControl(payment: IInvoicePayments): FormControl<number | null> {
    const id = payment.id_payinvoice;
    let control = this.originControls.get(id);
    if (!control) {
      control = new FormControl<number | null>({
        value: payment.paymentType?.[0]?.id_payment ?? null,
        disabled: true,
      });
      this.originControls.set(id, control);
    }
    return control;
  }

  originOptions(payment: IInvoicePayments): SelectOption[] {
    return [
      {
        value: payment.paymentType?.[0]?.id_payment ?? 0,
        label: payment.paymentType?.[0]?.description ?? '-',
      },
    ];
  }

  isProcessingApproval(idPayinvoice: number): boolean {
    return this.processingApprovalIds().has(idPayinvoice);
  }

  getTransferDetails(idPayinvoice: number): ITransferDetails | null {
    return this.transferDetailsByPayment().get(idPayinvoice) ?? null;
  }

  private async loadPersistedTransferDetails(): Promise<void> {
    const pending = this.currentInvoicePayments().filter(
      (payment) =>
        payment.status >= APPLIED_PAYMENT_STATUS &&
        !this.transferDetailsByPayment().has(payment.id_payinvoice),
    );

    if (pending.length === 0) {
      return;
    }

    const found = await Promise.all(
      pending.map(async (payment) => {
        const details = await this.transfersService
          .getTransferDetailsByIdentifier(payment.identifier)
          .catch(() => null);
        return { id: payment.id_payinvoice, details };
      }),
    );

    this.transferDetailsByPayment.update((current) => {
      const next = new Map(current);
      found.forEach(({ id, details }) => {
        if (details) {
          next.set(id, details);
        }
      });
      return next;
    });
  }

  onTabChange(tabId: string): void {
    this.currentTabId.set(tabId);
  }

  async approveAndTransfer(payment: IInvoicePayments): Promise<void> {
    const id = payment.id_payinvoice;
    const destinationId = this.getDestinationControl(id).value;
    const originId = payment.paymentType?.[0]?.id_payment;

    if (!destinationId || !originId) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.transfer-destination-required',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    this.processingApprovalIds.update((current) => new Set(current).add(id));
    this.spinnerService.show();

    try {
      if (payment.status === 1) {
        await firstValueFrom(
          this.portfolio.updateStatusPayment(id, true, false),
        );
      }
      await firstValueFrom(this.portfolio.updateStatusPayment(id, false, true));
    } catch (error: unknown) {
      this.spinnerService.hide();
      this.processingApprovalIds.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      const message =
        (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
        (error as Error)?.message ||
        this.translate.instant('global.alerts.title.error');
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message,
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    try {
      await this.transfersService.createSimpleTransfer(
        'Portfolio',
        new Date().toDateString(),
        payment.value,
        id,
        originId,
        destinationId,
      );
    } catch {
      this.spinnerService.hide();
      this.processingApprovalIds.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      return;
    }

    const originLabel = payment.paymentType?.[0]?.description ?? '';
    const destinationLabel =
      this.destinationOptions.find(
        (option) => String(option.value) === String(destinationId),
      )?.label ?? '';

    this.transferDetailsByPayment.update((current) => {
      const next = new Map(current);
      next.set(id, { originLabel, destinationLabel });
      return next;
    });

    this.processingApprovalIds.update((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    this.destinationControls.delete(id);
    this.originControls.delete(id);
    this.currentInvoicePayments.set(
      await firstValueFrom(this.portfolio.getPaymenInvoice(this.idFactura())),
    );
    this.buildTabs();
    // Approving is what moves estado_factura in Cartera, so the status tile
    // reads the state back here as the register and delete paths do.
    await this.refreshInvoiceState();
    this.sended = true;
    this.refresh = true;
    this.spinnerService.hide();

    const isInvoiceComplete =
      round2(this.paidQuot()) >= round2(this.totalQuot()) &&
      !!this.quotationObj().payment &&
      this.paymentsService.validCompletePayments(this.currentInvoicePayments());

    if (isInvoiceComplete) {
      try {
        await this.changeStatus();
      } catch (error: unknown) {
        const message =
          (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
          (error as Error)?.message ||
          this.translate.instant('global.alerts.title.error');
        this.alertService.open({
          title: this.translate.instant('payments.error.title'),
          message,
          type: 'error',
          acceptText: 'Ok',
        });
      }
    }

    this.approvalConfirmation.set({
      isInvoiceComplete,
      value: payment.value,
      originLabel,
      destinationLabel,
    });
    timer(APPROVAL_CONFIRMATION_DURATION_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.approvalConfirmation.set(null));
  }

  async listsData() {
    // Independent reads — none of these depend on each other's result.
    const [listCashiers, listBanks] = await Promise.all([
      this.paymentsService.getCashierList(),
      this.paymentsService.getBankList(),
      this.getAdvancePayments(),
    ]);
    this.listCashiers.set(listCashiers);
    this.listBanks.set(listBanks);
  }

  async getAdvancePayments() {
    this.advancePayments.set(
      await this.paymentsService.getAdvancePayments(
        this.idCustomer(),
        null,
        AdvancePaymentOwnerType.CUSTOMERS,
      ),
    );
  }

  async changeStatus(): Promise<void> {
    this.spinnerService.show();
    const imageElement = this.paymentsPdfService.getCompanyLogo();
    await new Promise<void>((resolve, reject) => {
      // getQuotation is a watch query: it emits again whenever the invoice is
      // re-read (after a delete or a new payment). takeUntilDestroyed only
      // stops that on close, so take(1) keeps a later re-read from closing
      // the invoice again.
      this.quotationService
        .getQuotation(this.quotation().id_factura!)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: async (result) => {
            try {
              await this.afterChangeStatus(imageElement, result);
              resolve();
            } catch (error) {
              this.spinnerService.hide();
              reject(error);
            }
          },
          error: (error) => {
            this.spinnerService.hide();
            this.alertService.open({
              title: 'Error',
              message: this.translate.instant('Something was wrong'),
              type: 'error',
            });
            reject(error);
          },
        });
    });
  }

  async afterChangeStatus(
    imageElement: string | undefined,
    result: IQuotation,
  ): Promise<void> {
    this.quotation.set(result);
    const paymentsDescriptions: string[] = this.quotationObj().payment!.map(
      (payment) => payment.description,
    );
    const getPdf = await this.paymentsPdfService.createPaymentInvoicePdf(
      false,
      'Cancelled',
      this.quotation(),
      imageElement,
      paymentsDescriptions.join(', '),
    );
    if (!getPdf) {
      this.spinnerService.hide();
      return;
    }
    await new Promise<void>((resolve, reject) => {
      getPdf.getBase64((data) => {
        this.quotationService
          .updateStatusInvoice(this.idFactura(), false, true, data)
          .subscribe({
            next: (result) => {
              this.afterUpdateStatusInvoice(result);
              resolve();
            },
            error: (error) => {
              this.spinnerService.hide();
              this.alertService.open({
                title: 'Error',
                message: this.translate.instant('Something was wrong'),
                type: 'error',
              });
              reject(error);
            },
          });
      });
    });
  }

  afterUpdateStatusInvoice(result: unknown) {
    if (result) {
      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('Cancelled'),
        message: this.translate.instant('Status has been changed succesfully.'),
        type: 'success',
        acceptText: 'Ok',
      });
      this.refresh = true;
    } else {
      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('Something was wrong'),
        message: this.translate.instant('error trying to update the quotation'),
        type: 'error',
        acceptText: 'Ok',
      });
    }
  }

  // pendingBefore must be read before paidQuot changes: missingPaid is
  // computed, so reading it afterwards counts the payment as surplus.
  async handlePositiveBalanceUpdate(
    value: number,
    pendingBefore: number,
  ): Promise<void> {
    if (value > pendingBefore) {
      await this.adjustPositiveBalance(value - pendingBefore);
    }
  }

  private async adjustPositiveBalance(
    delta: number,
    notifySuccess = true,
  ): Promise<void> {
    if (round2(delta) === 0) {
      return;
    }

    const current = await this.paymentsService.getPositiveBalance(
      this.idCustomer(),
    );
    const next = round2(current + delta);
    const success = await this.paymentsService.updatePositiveBalanceByCustomer(
      this.idCustomer(),
      next,
      notifySuccess,
    );
    this.positiveBalanceValue.set(
      success
        ? next
        : await this.paymentsService.getPositiveBalance(this.idCustomer()),
    );
  }

  // Persists the current balance — only call this after a payment is
  // actually registered or deleted, never on a read-only load.
  updateTotalPaidAndPositiveBalance() {
    this.updatePositiveBalanceByInvoice(
      this.idFactura(),
      this.totalPositivebalance(),
    );
  }

  updatePositiveBalanceByInvoice(idFactura: number, positiveBalance: number) {
    this.paymentsService.updatePositiveBalanceByInvoice(
      idFactura,
      positiveBalance,
    );
  }

  private get activeCancellationPaymentId(): number | null {
    return findActiveCancellationId(
      this.currentInvoicePayments().map((payment) => ({
        id: payment.id_payinvoice,
        status: payment.status,
        type: payment.type,
      })),
    );
  }

  // Payments can be deleted in any order. The one exception: while an
  // approved Cancellation payment exists on the invoice, only that
  // cancellation can be deleted — every other payment (earlier or later)
  // stays locked until it is removed, since deleting it is what re-opens
  // the invoice for further payment changes. Confirmed with business.
  canDeletePayment(idPayinvoice: number): boolean {
    return canDeletePaymentRule(idPayinvoice, this.activeCancellationPaymentId);
  }

  isPendingDeleteConfirmation(idPayinvoice: number): boolean {
    return this.deleteState.isPendingConfirmation(idPayinvoice);
  }

  isDeletingPayment(idPayinvoice: number): boolean {
    return this.deleteState.isProcessing(idPayinvoice);
  }

  requestDeletePayment(idPayinvoice: number): void {
    this.deleteState.request(idPayinvoice);
  }

  cancelDeletePayment(): void {
    this.deleteState.cancel();
  }

  confirmDeletePayment(paymentDelete: IInvoicePayments): void {
    const id = paymentDelete.id_payinvoice;
    this.deleteState.beginProcessing(id);
    const excess =
      paymentDelete.payment_description === PaymentEntryType.POSITIVE_BALANCE
        ? 0
        : this.excessGeneratedBy(this.paidQuot(), paymentDelete.value);
    if (excess <= 0) {
      this.removePayment(id, paymentDelete);
      return;
    }
    void this.removeOverpayment(id, paymentDelete, excess);
  }

  private async removeOverpayment(
    id: number,
    paymentDelete: IInvoicePayments,
    excess: number,
  ): Promise<void> {
    let currentBalance: number;
    try {
      currentBalance = await this.paymentsService.getPositiveBalance(
        this.idCustomer(),
      );
    } catch (error) {
      this.deleteState.endProcessing(id);
      console.error('Error reading the positive balance before delete', error);
      this.alertService.open({
        title: 'Error',
        message: this.translate.instant('Something was wrong'),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    if (round2(currentBalance) < excess) {
      this.deleteState.endProcessing(id);
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.delete-overpayment-balance-used',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    this.removePayment(id, paymentDelete);
  }

  removePayment(id: number, paymentDelete: IInvoicePayments) {
    this.portfolio
      .removePaymentInvoice(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.deleteState.endProcessing(id);
          if (!result) {
            this.alertService.open({
              title: 'Error',
              message: this.translate.instant('Something was wrong'),
              type: 'error',
              acceptText: 'Ok',
            });
            return;
          }
          this.afterRemovePayment(result, paymentDelete).catch((error) => {
            console.error('Error finishing payment removal', error);
            this.alertService.open({
              title: 'Error',
              message: this.translate.instant('Something was wrong'),
              type: 'error',
              acceptText: 'Ok',
            });
          });
          this.alertService.open({
            title: this.translate.instant('Good'),
            message: this.translate.instant('Payment removed'),
            type: 'success',
            acceptText: 'Ok',
          });
        },
        error: (error) => {
          this.deleteState.endProcessing(id);
          console.error('Error removing payment', error);
          this.alertService.open({
            title: 'Error',
            message: this.translate.instant('Something was wrong'),
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  async afterRemovePayment(
    result: IInvoicePayments,
    paymentDelete: IInvoicePayments,
  ): Promise<void> {
    this.paidQuot.update((current) => current - result.value);
    try {
      await this.deletePositiveBalance(paymentDelete);
    } catch (error) {
      console.error(
        'Error reversing positive balance after payment removal',
        error,
      );
      this.alertService.open({
        title: 'Error',
        message: this.translate.instant('Something was wrong'),
        type: 'error',
        acceptText: 'Ok',
      });
    } finally {
      this.updateTotalPaidAndPositiveBalance();
      this.updatePayments();
      this.refresh = true;
      this.getAdvancePayments();
      await this.refreshInvoiceState();
    }
  }

  // Re-read from the server after anything that moves the balance. Three
  // things depend on it: the backend drops a payment method once the last
  // payment using it is deleted, so the local guard would otherwise claim it
  // is still assigned; estado_factura is derived there, so this is where the
  // modal learns the state it reached; and quotationObj holds the methods the
  // receipt lists and the completion guard checks, which were read once on
  // load and went stale as soon as a payment was registered or deleted.
  //
  // Deliberately narrow: the balance figures stay as they were computed for
  // this session, so nothing the user is looking at moves under them.
  private async refreshInvoiceState(): Promise<void> {
    try {
      const invoice = await this.paymentsService.getInvoice(this.idFactura());
      const methods: { id_payment: number }[] = invoice.payment ?? [];
      this.assignedPaymentIds = new Set(methods.map((item) => item.id_payment));
      this.documentState.set(invoice.estado_factura ?? null);
      this.quotationObj.set(invoice);
    } catch (error) {
      console.error('Error refreshing the invoice state', error);
    }
  }

  private async deletePositiveBalance(
    paymentDelete: IInvoicePayments,
  ): Promise<void> {
    if (
      paymentDelete.payment_description === PaymentEntryType.POSITIVE_BALANCE
    ) {
      await this.adjustPositiveBalance(paymentDelete.value);
      return;
    }

    // Takes back only the excess this payment put on the positive balance.
    // paidQuot no longer counts the payment (afterRemovePayment subtracts it
    // first), so it is added back to measure the excess it had generated.
    // Silent: confirmDeletePayment already checked the balance can absorb it,
    // and "Customer updated" would read as something the user did.
    await this.adjustPositiveBalance(
      -this.excessGeneratedBy(
        this.paidQuot() + paymentDelete.value,
        paymentDelete.value,
      ),
      false,
    );
  }

  /**
   * The part of a payment of `value` that went to the positive balance: how
   * far the paid total went over the invoice total with the payment, minus
   * how far it goes without it. `paidWith` must still include the payment.
   *
   * On delete the reversal passes `paidQuot() + value` on purpose: by then
   * afterRemovePayment has already subtracted the payment, and recomputing
   * from the live total is what keeps two payments deleted back to back from
   * each reverting the same excess. Don't swap it for a paid total captured
   * before the delete.
   */
  private excessGeneratedBy(paidWith: number, value: number): number {
    const excessOf = (paid: number) =>
      Math.max(0, round2(paid - this.totalQuot()));
    return round2(excessOf(paidWith) - excessOf(paidWith - value));
  }

  updatePayments() {
    this.portfolio
      .getPaymenInvoice(this.idFactura())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.currentInvoicePayments.set(result);
          this.buildTabs();
        },
        error: (error) => {
          console.error('Error refreshing invoice payments', error);
          this.alertService.open({
            title: this.translate.instant('payments.error.title'),
            message: this.translate.instant('global.alerts.title.error'),
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  calculateAvailable(total: number, used: number): number {
    return calculateAvailable(total, used);
  }

  onPayFullAmountToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (this.useAdvancePayment()) {
      if (checked) {
        this.paymentValueControl.setValue(
          this.selectedAdvancePaymentAvailable || null,
        );
      } else if (
        valueMatchesRoundedTarget(
          this.paymentValueControl.value,
          this.selectedAdvancePaymentAvailable,
        )
      ) {
        this.paymentValueControl.setValue(null);
      }
      this.payFullAmount.set(checked);
      return;
    }

    if (checked) {
      this.paymentValueControl.setValue(this.remainingAfterBalanceApplications);
    } else if (
      this.matchesRemainingAfterBalance(this.paymentValueControl.value)
    ) {
      this.paymentValueControl.setValue(null);
    }
  }

  private syncPayFullAmountWithValue(value: string | number | null): void {
    if (this.useAdvancePayment()) {
      this.advancePayment.syncFullAdvanceFlags(value);
      return;
    }
    this.payFullAmount.set(this.matchesRemainingAfterBalance(value));
  }

  private matchesRemainingAfterBalance(value: string | number | null): boolean {
    return valueMatchesRoundedTarget(
      value,
      this.remainingAfterBalanceApplications,
    );
  }

  private matchesMissingPaid(value: string | number | null): boolean {
    return valueMatchesRoundedTarget(value, this.missingPaid());
  }

  get paymentMethodOptions(): SelectOption[] {
    return this.advancePayment.paymentMethodOptions;
  }

  get bankOrCashierOptions(): SelectOption[] {
    return this.advancePayment.bankOrCashierOptions;
  }

  get invoiceConsecutiveNumber(): string {
    return (
      this.quotation().contfac?.invoice ||
      this.quotation().contfac?.quotation ||
      ''
    );
  }

  get dianInvoiceNumber(): string {
    const electronicNumber = this.quotation().billyInvoice?.invoice_number;

    if (
      !electronicNumber ||
      electronicNumber.trim() === STRING_CONSTANTS.PENDING
    ) {
      return '';
    }

    return electronicNumber;
  }

  get customerDisplayName(): string {
    const customer = this.quotation().cliente;
    if (!customer?.nombre) {
      return '';
    }

    return customer.doc_nit
      ? `${customer.nombre} - ${customer.doc_nit}`
      : customer.nombre;
  }

  get customerName(): string {
    return this.quotation().cliente?.nombre || '-';
  }

  get customerNit(): string {
    return this.quotation().cliente?.doc_nit || '-';
  }

  get sellerName(): string {
    return this.quotation().seller?.nombre || '-';
  }

  get sortedInvoicePayments(): (IInvoicePayments & {
    isFullPayment: boolean;
  })[] {
    let cumulativePaid = 0;
    return sortPaymentsByDate(this.currentInvoicePayments()).map((payment) => {
      cumulativePaid += payment.value;
      return { ...payment, isFullPayment: cumulativePaid >= this.totalQuot() };
    });
  }

  async addPayment(): Promise<void> {
    if (this.isAddingPayment()) {
      return;
    }
    this.isAddingPayment.set(true);

    try {
      await this.addPaymentEntry();
    } finally {
      this.isAddingPayment.set(false);
    }
  }

  private async addPaymentEntry(): Promise<void> {
    const paymentMethod = this.paymentMethodOptions.find(
      (option) => option.value === this.paymentMethodControl.value,
    );
    const bankOrCashier = this.bankOrCashierOptions.find(
      (option) => option.value === this.bankOrCashierControl.value,
    );
    const idPayment = this.bankOrCashierControl.value;
    const paymentMethodType = this.paymentMethodControl.value;
    const rawValue = this.useAdvancePayment()
      ? this.advanceValueControl.value
      : this.paymentValueControl.value;
    const value =
      typeof rawValue === 'number' ? rawValue : parseFloat(rawValue ?? '');

    if (
      !paymentMethod ||
      !bankOrCashier ||
      idPayment === null ||
      paymentMethodType === null ||
      !value ||
      isNaN(value)
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.add-payment-missing-fields',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    if (value < 0) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.payment-value-not-positive',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    const advanceTreasury = this.useAdvancePayment()
      ? this.advancePayments().find(
          (candidate) =>
            candidate.id_treasury === this.selectedAdvancePaymentId(),
        )
      : undefined;

    const rawManualValue = this.paymentValueControl.value;
    const manualValue =
      typeof rawManualValue === 'number'
        ? rawManualValue
        : parseFloat(rawManualValue ?? '');

    if (
      advanceTreasury &&
      (value > this.selectedAdvancePaymentAvailable ||
        manualValue > this.selectedAdvancePaymentAvailable)
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.advance-value-exceeds',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    const usesCreditBalance = this.useCreditBalance();

    if (usesCreditBalance && value > this.selectedCreditBalanceAvailable) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.advance-value-exceeds',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    const registeredValue =
      advanceTreasury && manualValue > 0 ? manualValue : value;

    // A line may pay more than what is left (registerPayments credits the
    // excess to the customer's positive balance), but only while something is
    // still left: once the invoice and the lines already added cover it, the
    // backend refuses any further payment too. An advance or the positive
    // balance itself never overpays — that would only move money from one
    // balance to the other.
    const remainingBeforeLine = round2(
      this.missingPaid() - this.addedPaymentsTotal,
    );
    const overpays =
      round2(registeredValue) > remainingBeforeLine + INVOICE_BALANCE_EPSILON;
    if (
      remainingBeforeLine <= INVOICE_BALANCE_EPSILON ||
      ((advanceTreasury || usesCreditBalance) && overpays)
    ) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.payment-value-exceeds-balance',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    if (!this.assignedPaymentIds.has(idPayment)) {
      this.spinnerService.show();
      try {
        await this.paymentsService.ensurePaymentTypeAssigned(
          this.idFactura(),
          idPayment,
          paymentMethodType,
        );
        this.assignedPaymentIds.add(idPayment);
      } catch {
        this.alertService.open({
          title: this.translate.instant('payments.error.title'),
          message: this.translate.instant('global.alerts.title.error'),
          type: 'error',
          acceptText: 'Ok',
        });
        return;
      } finally {
        this.spinnerService.hide();
      }
    }

    this.addedPayments.update((entries) => [
      ...entries,
      {
        id: `local-${this.nextLocalPaymentId++}`,
        id_payment: idPayment,
        paymentMethodType,
        paymentMethodLabel: paymentMethod.label,
        bankOrCashierLabel: bankOrCashier.label,
        value: registeredValue,
        date: this.dateControl.value ?? getTodayIsoDate(),
        id_ref: this.referenceControl.value ?? '',
        type:
          this.matchesMissingPaid(registeredValue) || overpays
            ? PaymentEntryType.CANCELLATION
            : PaymentEntryType.CREDIT,
        treasury: advanceTreasury,
        usesCreditBalance,
      },
    ]);

    this.resetAddPaymentForm();
    if (advanceTreasury) {
      this.removeAdvancePaymentSelection();
    }
    if (usesCreditBalance) {
      this.removeCreditBalanceSelection();
    }
  }

  removePaymentEntry(id: string): void {
    this.addedPayments.update((entries) =>
      entries.filter((entry) => entry.id !== id),
    );
  }

  protected handleCloseButtonClick(): void {
    this.closeModal()?.();
  }

  protected async generateReceipt(): Promise<void> {
    // Open before any await: mobile browsers block tabs opened outside the tap.
    const receiptWindow = this.pdfGeneratorService.openPendingWindow();

    try {
      const fullQuotation = (await firstValueFrom(
        this.quotationService.getQuotation(this.idFactura()),
      )) as IQuotation;

      const paymentsDescriptions = (fullQuotation.payment ?? []).map(
        (payment) => payment.description,
      );

      await this.paymentsPdfService.createPaymentInvoicePdf(
        true,
        'Cancelled',
        fullQuotation,
        this.paymentsPdfService.getCompanyLogo(),
        paymentsDescriptions.join(', '),
        receiptWindow,
      );
    } catch (error) {
      console.error('Error generating payment receipt:', error);
      this.pdfGeneratorService.closePendingWindow(receiptWindow);
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.generate-receipt-error',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
    }
  }

  private clearBalanceApplicationSelection(): void {
    this.useAdvancePayment.set(false);
    this.useCreditBalance.set(false);
    this.selectedAdvancePaymentId.set(null);
    this.useFullCreditBalanceAvailable.set(true);
    this.useFullAdvanceAvailable.set(false);
  }

  async registerPayments(): Promise<void> {
    if (this.isSubmittingPayments()) {
      return;
    }

    const entries = this.addedPayments();

    if (entries.length === 0) {
      this.alertService.open({
        title: this.translate.instant('payments.error.title'),
        message: this.translate.instant(
          'global.payments.no-payments-to-register',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }

    this.isSubmittingPayments.set(true);
    this.spinnerService.show();

    const remaining = [...entries];
    let registeredAny = false;
    for (const entry of entries) {
      try {
        const result = await firstValueFrom(
          this.portfolio.createPaymentInvoice(
            this.idFactura(),
            entry.id_payment,
            entry.type,
            toStoredDateString(entry.date),
            entry.value,
            entry.id_ref,
            entry.usesCreditBalance
              ? PaymentEntryType.POSITIVE_BALANCE
              : entry.type,
          ),
        );
        if (entry.treasury) {
          await this.paymentsService.relateAdvancePayment(
            AdvancePaymentAction.SET,
            AdvancePaymentSubject.INVOICE,
            result.id_payinvoice,
            entry.treasury.id_treasury,
            entry.value,
          );
        }
        if (entry.usesCreditBalance) {
          await this.adjustPositiveBalance(-entry.value);
        }
        const pendingBefore = this.missingPaid();
        this.paidQuot.update((current) => current + result.value);
        await this.handlePositiveBalanceUpdate(entry.value, pendingBefore);
        this.updateTotalPaidAndPositiveBalance();
        registeredAny = true;
        remaining.shift();
      } catch (error: unknown) {
        this.spinnerService.hide();
        this.isSubmittingPayments.set(false);
        this.addedPayments.set(remaining);
        if (registeredAny) {
          this.clearBalanceApplicationSelection();
          await this.getAdvancePayments();
          this.updatePayments();
          await this.refreshInvoiceState();
          this.sended = true;
          this.refresh = true;
          this.paymentRegistered = true;
          this.onPaymentRegistered()?.();
        }
        const message =
          (error as GraphQLErrorResponse)?.graphQLErrors?.[0]?.message ||
          (error as Error)?.message ||
          this.translate.instant('global.alerts.title.error');
        this.alertService.open({
          title: this.translate.instant('payments.error.title'),
          message,
          type: 'error',
          acceptText: 'Ok',
        });
        return;
      }
    }

    this.spinnerService.hide();
    this.isSubmittingPayments.set(false);
    this.addedPayments.set([]);
    this.clearBalanceApplicationSelection();
    await this.getAdvancePayments();
    this.updatePayments();
    await this.refreshInvoiceState();
    this.sended = true;
    this.refresh = true;
    this.paymentRegistered = true;
    this.onPaymentRegistered()?.();

    this.alertService.open({
      title: this.translate.instant('Created'),
      message: this.translate.instant('Payment created successfully!'),
      type: 'success',
      acceptText: 'Ok',
    });
  }

  get advancePaymentTotal(): number {
    return advancePaymentTotalUtil(this.advancePayments());
  }

  get hasAdvancePayment(): boolean {
    return hasAdvancePaymentUtil(this.advancePayments());
  }

  get hasSingleAdvancePayment(): boolean {
    return this.advancePayments().length === 1;
  }

  get hasMultipleAdvancePayments(): boolean {
    return hasMultipleAdvancePaymentsUtil(this.advancePayments());
  }

  get hasCreditBalance(): boolean {
    return this.positiveBalanceValue() > 0;
  }

  // Only a prefill for the bank/cashier fields when the invoice already
  // carries a method: the credit balance does not need one to be offered,
  // since registering the payment asks for a method like any other.
  private get firstAssignedPaymentId(): number | null {
    return this.quotationObj().payment?.[0]?.id_payment ?? null;
  }

  get appliedPaidTotal(): number {
    return sumAppliedPayments(this.currentInvoicePayments());
  }

  private get addedPaymentsTotal(): number {
    return this.addedPayments().reduce((sum, entry) => sum + entry.value, 0);
  }

  get pendingApprovalTotal(): number {
    return sumPendingApprovalPayments(this.currentInvoicePayments());
  }

  get appliedPendingBalance(): number {
    return appliedPendingBalanceUtil(this.totalQuot(), this.appliedPaidTotal);
  }

  get isFullyPaid(): boolean {
    // The balance fallback needs the total: payments arrive before it does,
    // and against a total of 0 any applied payment would read as Pagada.
    const state = this.documentState();
    return state === null
      ? this.invoiceTotalLoaded() &&
          isFullyPaidUtil(this.totalQuot(), this.appliedPaidTotal)
      : state === DOCUMENT_STATE.PAID;
  }

  get hasAssociatedPayments(): boolean {
    const state = this.documentState();
    return state === null
      ? hasAssociatedPaymentsUtil(this.appliedPaidTotal)
      : state === DOCUMENT_STATE.PARTIAL || state === DOCUMENT_STATE.PAID;
  }

  get isVoidedInvoice(): boolean {
    const state = this.documentState();
    return state === null
      ? isInvoiceVoided(this.totalQuot(), this.hasAssociatedPayments)
      : state === DOCUMENT_STATE.VOID;
  }

  onToggleAdvancePayment(event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      this.removeCreditBalanceSelection();
    }

    this.useAdvancePayment.set(checked);

    if (checked && this.hasSingleAdvancePayment) {
      this.selectAdvancePayment(this.advancePayments()[0].id_treasury);
    } else {
      this.selectAdvancePayment(null);
    }
    if (!checked) {
      this.paymentValueControl.setValue(null);
    }
  }

  onToggleCreditBalance(event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      this.removeAdvancePaymentSelection();
    }

    this.useCreditBalance.set(checked);
    this.useFullCreditBalanceAvailable.set(true);
    this.paymentValueControl.setValue(
      checked ? this.selectedCreditBalanceAvailable : null,
    );
    this.syncPaymentOriginDisplay(checked ? this.firstAssignedPaymentId : null);
  }

  onToggleFullCreditBalanceAmount(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.useFullCreditBalanceAvailable.set(checked);

    if (checked) {
      this.paymentValueControl.setValue(this.selectedCreditBalanceAvailable);
    }
  }

  onSelectAdvancePayment(idTreasury: number): void {
    this.selectAdvancePayment(idTreasury);
  }

  onToggleFullAdvanceAmount(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.useFullAdvanceAvailable.set(checked);

    if (checked) {
      this.advanceValueControl.setValue(this.selectedAdvancePaymentAvailable);
    }
  }

  removeAdvancePaymentSelection(): void {
    this.useAdvancePayment.set(false);
    this.selectAdvancePayment(null);
  }

  removeCreditBalanceSelection(): void {
    this.useCreditBalance.set(false);
    this.syncPaymentOriginDisplay(null);
  }

  removeBalanceApplicationSelection(): void {
    this.removeAdvancePaymentSelection();
    this.removeCreditBalanceSelection();
    this.paymentValueControl.reset(null);
  }

  get selectedAdvancePaymentAvailable(): number {
    return this.advancePayment.selectedAdvancePaymentAvailable;
  }

  get selectedCreditBalanceAvailable(): number {
    return this.paymentsService.getTotalWithAdvancePayment(
      this.missingPaid(),
      this.positiveBalanceValue(),
    );
  }

  get creditBalanceAvailable(): number {
    const rawValue = this.paymentValueControl.value;
    const used =
      typeof rawValue === 'number' ? rawValue : parseFloat(rawValue ?? '');
    return round2(this.positiveBalanceValue() - (isNaN(used) ? 0 : used));
  }

  // Invoice balance still to pay minus the lines already added; available
  // advances / credit balances only count once the user adds them.
  get remainingAfterBalanceApplications(): number {
    return Math.max(0, round2(this.missingPaid() - this.addedPaymentsTotal));
  }

  // Only an advance payment fixes where the money came from, so only it locks
  // the bank/cashier field. A credit balance has no bank behind it — the user
  // chooses where the payment is recorded, and the invoice's first method is
  // offered as an editable default.
  get paymentMethodsDisabled(): boolean {
    return this.useAdvancePayment();
  }

  get manualValueFieldLocked(): boolean {
    return this.useCreditBalance() && this.useFullCreditBalanceAvailable();
  }

  private selectAdvancePayment(idTreasury: number | null): void {
    this.useFullAdvanceAvailable.set(false);
    this.advancePayment.selectAdvancePayment(idTreasury);
    if (this.useAdvancePayment()) {
      return;
    }
    this.payFullAmount.set(
      this.matchesRemainingAfterBalance(this.paymentValueControl.value),
    );
  }

  private syncPaymentOriginDisplay(idPayment: number | null): void {
    this.advancePayment.syncPaymentOriginDisplay(idPayment);
  }

  private resetAddPaymentForm(): void {
    this.paymentMethodControl.reset(null);
    this.bankOrCashierControl.reset(null);
    this.paymentValueControl.reset(null);
    this.dateControl.reset(getTodayIsoDate());
    this.referenceControl.reset('');
  }

  ngOnDestroy(): void {
    this.onClose()?.({
      sended: this.sended,
      refresh: this.refresh,
      notifyState: this.paymentRegistered ? this.documentState() : null,
    });
  }
}
